/**
 * 订单详情抽屉 - 订单审核区块
 *
 * **只在「待审核」时被渲染**：审核完成后（已通过/已拒绝）这里不再出整块面板，
 * 结果收敛为徽章行的一枚审核徽标（`BadgesSection`），拒绝理由跟着徽标留一行。
 * 本区块因此只剩待确认态：状态 + 一句说明 + 对方的历史审核意见（改派重提时看得见上一轮的理由）
 * + 客户方视角下的"确认通过/拒绝"操作与违约风险提示。受控组件，不持有业务状态。
 */
import type { OrderItem } from '../../../../../types';
import type { OrderCancelRisk } from '../../../../../api/orderApi';

interface Props {
  order: OrderItem;
  reviewStatus: string;
  reviewStyle: { bg: string; text: string; icon: string; label: string } | null;
  isCustomerOfOrder: boolean;
  isCancelled: boolean;
  showRejectInput: boolean;
  rejectReason: string;
  reviewLoading: boolean;
  /** 审核动作失败的原文：档位不动、面板不收，就地留一句红字（只 console.error 等于「点了没反应」） */
  reviewError: string;
  /** 提交方的取消留痕读数：仅达阈值时有一句 message，未达阈值/未读到时都不占版面 */
  cancelRisk: OrderCancelRisk | null;
  /** 读失败与读到了「没风险」是两回事，失败要在审核框里留一句话 */
  cancelRiskFailed: boolean;
  onRejectReasonChange: (value: string) => void;
  onSetShowRejectInput: (value: boolean) => void;
  onApprove: () => void;
  onReject: () => void;
}

export default function ReviewSection({
  order,
  reviewStatus,
  reviewStyle,
  isCustomerOfOrder,
  isCancelled,
  showRejectInput,
  rejectReason,
  reviewLoading,
  reviewError,
  cancelRisk,
  cancelRiskFailed,
  onRejectReasonChange,
  onSetShowRejectInput,
  onApprove,
  onReject,
}: Props) {
  return (
    <>
      {/* Review */}
      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">订单审核</p>
        <div className={`rounded-xl border p-4 ${reviewStyle?.bg ?? 'bg-gray-50 border-gray-200'}`}>
          <div className="flex items-center gap-3 mb-2">
            <div className={`w-9 h-9 flex items-center justify-center rounded-full border-2 shrink-0 ${reviewStyle?.bg ?? ''} ${reviewStyle?.text ?? ''}`}>
              <i className={`${reviewStyle?.icon ?? 'ri-shield-line'} text-base`} />
            </div>
            <div>
              <p className={`text-sm font-semibold ${reviewStyle?.text ?? 'text-gray-600'}`}>{reviewStyle?.label ?? reviewStatus}</p>
              <p className="text-xs text-gray-400 mt-0.5">等待客户方确认此订单，避免重复录单</p>
            </div>
          </div>
          {order.reviewComment && <p className="mt-2 text-sm text-gray-600 bg-white/60 rounded-lg px-3 py-2 border border-gray-100">备注：{order.reviewComment}</p>}
          {/* 违约风险提示：判据与文案都在服务端，这里只负责在「要点通过/拒绝」的那一格里摆出来 */}
          {cancelRisk?.atRisk && (
            <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-100 bg-amber-50 px-3 py-2">
              <i className="ri-error-warning-line text-amber-500 text-sm mt-0.5 shrink-0" />
              <p className="text-xs leading-relaxed text-amber-700">
                {cancelRisk.message}
                <span className="text-amber-500">（窗口与阈值可在管理端《系统设置 · 订单策略》调整）</span>
              </p>
            </div>
          )}
          {cancelRiskFailed && !cancelRisk?.atRisk && (
            <p className="mt-2 text-xs text-gray-400">违约风险提示读取失败，不影响审核，可稍后重开本单再看</p>
          )}
          {isCustomerOfOrder && reviewStatus === '待审核' && !isCancelled && (
            <div className="mt-3 pt-3 border-t border-gray-200/60 space-y-2">
              <p className="text-xs text-gray-500">你是此订单的客户，请确认订单信息是否正确：</p>
              {showRejectInput ? (
                <div className="space-y-2">
                  <textarea value={rejectReason} onChange={e => onRejectReasonChange(e.target.value)} placeholder="请填写拒绝原因（选填）" rows={2} maxLength={200} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-200 focus:border-rose-300 resize-none" />
                  <div className="flex gap-2">
                    <button onClick={() => onSetShowRejectInput(false)} disabled={reviewLoading} className="flex-1 py-2 text-sm border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50 cursor-pointer transition-colors whitespace-nowrap disabled:opacity-50">取消</button>
                    <button onClick={onReject} disabled={reviewLoading} className="flex-1 py-2 text-sm bg-rose-500 text-white rounded-lg hover:bg-rose-600 cursor-pointer transition-colors whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-1">
                      {reviewLoading && <i className="ri-loader-4-line animate-spin text-xs" />}确认拒绝
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button onClick={onApprove} disabled={reviewLoading} className="flex-1 py-2 text-sm bg-teal-500 text-white rounded-lg hover:bg-teal-600 cursor-pointer transition-colors whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-1.5">
                    {reviewLoading ? <i className="ri-loader-4-line animate-spin text-sm" /> : <i className="ri-check-line text-sm" />}确认通过
                  </button>
                  <button onClick={() => onSetShowRejectInput(true)} disabled={reviewLoading} className="flex-1 py-2 text-sm border border-rose-200 text-rose-500 rounded-lg hover:bg-rose-50 cursor-pointer transition-colors whitespace-nowrap disabled:opacity-50">拒绝</button>
                </div>
              )}
              {reviewError && (
                <p className="text-xs text-rose-600 flex items-start gap-1.5">
                  <i className="ri-error-warning-line text-xs mt-0.5 shrink-0" />
                  {reviewError}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}